import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import PasswordField from '../../components/shell/PasswordField';
import { StatusBadge } from '../../components/datagrid/DataGrid';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { CheckCircle2, ExternalLink, Train, AlertCircle, Save, RotateCcw, Plug, Info } from 'lucide-react';

export default function RailwaySettings() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [config, setConfig] = useState({
    enabled: false,
    api_token: '',
    api_token_preview: '',
    project_id: '',
    environment_id: '',
    service_id_frontend: '',
    target_host: '',
  });

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get('/admin/railway/config');
      setConfig(c => ({ ...c, ...res.data, api_token: '' }));
      setDirty(false);
    } catch {
      toast.error('Failed to load Railway config');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Field updater: patches config and marks the form dirty
  const updateConfig = (patch) => {
    setConfig(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send api_token if user typed a new one
      const payload = { ...config };
      delete payload.api_token_preview;
      if (!payload.api_token) delete payload.api_token;
      await api.put('/admin/railway/config', payload);
      toast.success('Railway config saved');
      // Refresh to get masked token
      const res = await api.get('/admin/railway/config');
      setConfig(c => ({ ...c, ...res.data, api_token: '' }));
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReload = async () => {
    setTestResult(null);
    await fetchConfig();
    toast.info('Configuration reloaded');
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/admin/railway/test');
      setTestResult({ success: true, ...res.data });
      toast.success(`Connected to Railway project: ${res.data.project_name}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Test failed';
      setTestResult({ success: false, error: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <RecordPage title="Railway domains" loading onBack={null} data-testid="railway-settings-page" />
      </DashboardLayout>
    );
  }

  const hasToken = config.api_token_preview && !config.api_token;

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="railway-settings-page"
        title="Railway domains"
        subtitle="Custom-domain automation via the Railway API"
        badge={<StatusBadge value={config.enabled ? 'enabled' : 'disabled'} />}
        onBack={null}
        onRefresh={fetchConfig}
        refreshing={loading}
        dirty={dirty}
        primary={[
          { key: 'save', label: saving ? 'Saving…' : 'Save', icon: Save, onClick: handleSave, disabled: saving, testid: 'save-railway-config-btn' },
          { key: 'test', label: testing ? 'Testing…' : 'Test connection', icon: Plug, variant: 'outline', onClick: handleTest, disabled: testing || !config.enabled, testid: 'test-railway-btn', title: config.enabled ? 'Verify the saved token and project IDs' : 'Enable the Railway API first' },
        ]}
        secondary={[
          { key: 'reload', label: 'Reload', icon: RotateCcw, onClick: handleReload, disabled: saving || testing, title: 'Reload saved configuration (discards unsaved edits)' },
        ]}
      >
        {testResult && (
          <div className={`rounded-lg border p-3 flex items-start gap-2 text-sm ${testResult.success ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`} data-testid="railway-test-result">
            {testResult.success
              ? <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              : <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
            <div className="min-w-0 flex-1">
              {testResult.success ? (
                <>
                  <p className="font-medium text-green-800">Connected!</p>
                  <p className="text-xs text-green-700">Project: <strong>{testResult.project_name}</strong></p>
                  <p className="text-xs text-green-700 mt-1">
                    Services: {(testResult.services || []).map(s => <Badge key={s.id} variant="outline" className="ml-1 text-[10px] border-green-400 text-green-700 bg-white">{s.name}</Badge>)}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium text-red-800">Connection failed</p>
                  <p className="text-xs text-red-700 mt-1">{testResult.error}</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* How it works */}
        <FormSection title="How this works" icon={Info} bodyClassName="p-4 text-sm">
          <ol className="list-decimal pl-5 space-y-1 text-muted-foreground text-xs">
            <li>Connect your Railway API token + project IDs below</li>
            <li>From <strong>Super Admin → Tenants</strong>, click "Add Custom Domain" on any tenant</li>
            <li>App calls Railway API → adds the domain to your frontend service automatically</li>
            <li>Tenant adds the CNAME + TXT records Railway returns (shown in tenant settings)</li>
            <li>Railway auto-issues SSL within ~5 min — no manual dashboard work</li>
          </ol>
        </FormSection>

        {/* Railway API credentials */}
        <FormSection
          title="Railway API"
          icon={Train}
          description="Enable programmatic custom-domain management via Railway's GraphQL API"
          actions={
            <Switch
              checked={config.enabled}
              onCheckedChange={(c) => updateConfig({ enabled: c })}
              data-testid="railway-enabled-switch"
            />
          }
          bodyClassName="p-3 space-y-3"
        >
          {/* API Token */}
          <div className="field">
  <PasswordField id="api_token" label="API Token" value={config.api_token} onChange={(v) => updateConfig({ api_token: v })} placeholder={hasToken ? config.api_token_preview : 'xxx-xxx-xxx-xxx (paste new token)'} minLength={1} autoComplete="off" testid="api-token-input" />
            <p className="field-hint">
              Create at <a href="https://railway.app/account/tokens" target="_blank" rel="noreferrer" className="text-violet-600 hover:underline inline-flex items-center gap-1">
                railway.app/account/tokens <ExternalLink className="w-3 h-3" />
              </a>. Leave blank to keep existing token.
            </p>
          </div>

          <Separator />

          {/* Project / Service / Environment IDs */}
          <div className="form-grid">
            <div className="field">
              <Label htmlFor="project_id">Project ID *</Label>
              <Input
                id="project_id"
                placeholder="01234567-89ab-cdef-0123-456789abcdef"
                value={config.project_id || ''}
                onChange={(e) => updateConfig({ project_id: e.target.value })}
                data-testid="project-id-input"
                className="font-mono text-xs"
              />
            </div>
            <div className="field">
              <Label htmlFor="environment_id">Environment ID *</Label>
              <Input
                id="environment_id"
                placeholder="UUID of production environment"
                value={config.environment_id || ''}
                onChange={(e) => updateConfig({ environment_id: e.target.value })}
                data-testid="environment-id-input"
                className="font-mono text-xs"
              />
            </div>
            <div className="field">
              <Label htmlFor="service_id">Frontend Service ID *</Label>
              <Input
                id="service_id"
                placeholder="UUID of frontend service"
                value={config.service_id_frontend || ''}
                onChange={(e) => updateConfig({ service_id_frontend: e.target.value })}
                data-testid="service-id-input"
                className="font-mono text-xs"
              />
            </div>
            <div className="field">
              <Label htmlFor="target_host">Fallback Target Host (optional)</Label>
              <Input
                id="target_host"
                placeholder="v90afqbw.up.railway.app"
                value={config.target_host || ''}
                onChange={(e) => updateConfig({ target_host: e.target.value })}
                data-testid="target-host-input"
                className="font-mono text-xs"
              />
              <p className="text-[10px] text-muted-foreground">Shown to tenants as the CNAME target value</p>
            </div>
          </div>

          <div className="rounded-lg border border-dashed bg-muted/30 p-3 text-xs space-y-1">
            <p className="font-medium">Where to find these IDs:</p>
            <p>Open your Railway project URL — it looks like:</p>
            <code className="block text-[10px] bg-white p-2 rounded border break-all">
              https://railway.com/project/<strong className="text-violet-600">[PROJECT_ID]</strong>?environmentId=<strong className="text-violet-600">[ENV_ID]</strong>&serviceId=<strong className="text-violet-600">[SERVICE_ID]</strong>
            </code>
            <p>Click on your <strong>frontend</strong> service to get the serviceId in the URL.</p>
          </div>
        </FormSection>
      </RecordPage>
    </DashboardLayout>
  );
}
